import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { formatEURDecimal } from "../lib/format";
import { Plus, Trash2, Check, X, Camera, Upload, RefreshCw, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

function OcrDialog({ open, onClose, onApplied }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [replace, setReplace] = useState(false);
  const dropRef = useRef(null);

  if (!open) return null;

  const handleFile = (f) => {
    setFile(f);
    setExtracted(null);
    if (f) {
      const r = new FileReader();
      r.onload = (e) => setPreview(e.target.result);
      r.readAsDataURL(f);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) handleFile(e.dataTransfer.files[0]);
  };

  const extract = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/banks/ocr-screenshot", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setExtracted(r.data.accounts || []);
      if ((r.data.accounts || []).length === 0) toast.warning("No se detectaron cuentas en la imagen");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al procesar la imagen");
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    try {
      const accounts = extracted.filter(a => a.name && !isNaN(Number(a.balance)));
      const r = await api.post("/banks/bulk-update", { accounts, replace });
      toast.success(`Aplicado: ${r.data.updated} actualizados, ${r.data.created} creados${r.data.deleted ? `, ${r.data.deleted} eliminados` : ""}`);
      onApplied();
    } catch (e) { toast.error("Error al aplicar"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose} data-testid="ocr-overlay">
      <div onClick={(e) => e.stopPropagation()} className="bg-white border border-[var(--border)] w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-[var(--border)]">
          <div>
            <div className="font-mono-num text-[10px] uppercase tracking-widest text-[var(--ink-muted)]">Actualizar saldos · OCR vía IA</div>
            <h3 className="font-display font-black text-2xl tracking-tight">Sube un pantallazo del banco.</h3>
          </div>
          <button onClick={onClose} className="text-sm border border-[var(--border)] px-3 py-1.5 hover:bg-[var(--hover)]" data-testid="ocr-close">Cerrar</button>
        </div>

        <div className="p-5 overflow-auto flex-1">
          {!extracted ? (
            <div
              ref={dropRef}
              onDragOver={(e) => e.preventDefault()}
              onDrop={onDrop}
              className="border-2 border-dashed border-[var(--border)] p-8 text-center"
              data-testid="ocr-drop"
            >
              {preview ? (
                <div>
                  <img src={preview} alt="" className="max-h-72 mx-auto border border-[var(--border)]" />
                  <p className="text-xs text-[var(--ink-muted)] font-mono-num mt-2">{file?.name}</p>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload size={32} strokeWidth={1.2} className="text-[var(--ink-muted)]" />
                  <p className="font-display font-bold">Arrastra un pantallazo o selecciónalo</p>
                  <p className="text-xs text-[var(--ink-muted)] font-mono-num">PNG / JPG / WEBP · max 8MB</p>
                </div>
              )}
              <label className="mt-4 inline-flex items-center gap-2 border border-[var(--border)] px-4 py-2 text-sm cursor-pointer hover:bg-[var(--hover)]" data-testid="ocr-select">
                <Upload size={14} /> Seleccionar imagen
                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0] || null)} data-testid="ocr-file-input" />
              </label>
              <div className="mt-6">
                <button onClick={extract} disabled={!file || loading} className="bg-black text-white px-6 py-2.5 text-sm disabled:opacity-40 hover:bg-gray-800" data-testid="ocr-extract">
                  {loading ? "Analizando con IA…" : "Extraer cuentas"}
                </button>
              </div>
            </div>
          ) : (
            <div data-testid="ocr-preview">
              <p className="text-sm mb-3">La IA detectó <strong>{extracted.length} cuentas</strong>. Revisa antes de aplicar:</p>
              <table className="w-full text-sm border border-[var(--border)]">
                <thead className="bg-[var(--hover)] text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-muted)]">
                  <tr>
                    <th className="text-left px-3 py-2">Cuenta</th>
                    <th className="text-right px-3 py-2">Saldo detectado</th>
                  </tr>
                </thead>
                <tbody>
                  {extracted.map((a, i) => (
                    <tr key={i} className="border-t border-[var(--border)]">
                      <td className="px-3 py-2">
                        <input
                          className="w-full border border-transparent hover:border-[var(--border)] focus:border-black px-1 py-0.5 focus:outline-none"
                          value={a.name}
                          onChange={(e) => setExtracted(extracted.map((x, j) => j === i ? { ...x, name: e.target.value } : x))}
                        />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <input
                          type="number" step="0.01"
                          className="w-full text-right font-mono-num border border-transparent hover:border-[var(--border)] focus:border-black px-1 py-0.5 focus:outline-none"
                          value={a.balance}
                          onChange={(e) => setExtracted(extracted.map((x, j) => j === i ? { ...x, balance: parseFloat(e.target.value) || 0 } : x))}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="mt-4 flex items-center justify-between flex-wrap gap-3">
                <label className="flex items-center gap-2 text-xs">
                  <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)} data-testid="ocr-replace" />
                  <span>Reemplazar todas las cuentas existentes (elimina las que no aparezcan en el pantallazo)</span>
                </label>
                <div className="flex gap-2">
                  <button onClick={() => { setExtracted(null); setPreview(null); setFile(null); }} className="border border-[var(--border)] px-4 py-2 text-sm hover:bg-[var(--hover)]" data-testid="ocr-back">Volver</button>
                  <button onClick={apply} className="bg-black text-white px-4 py-2 text-sm hover:bg-gray-800" data-testid="ocr-apply">Aplicar saldos</button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Bancos() {
  const nav = useNavigate();
  const [data, setData] = useState({ banks: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState({});
  const [newBank, setNewBank] = useState({ name: "", balance: "" });
  const [ocrOpen, setOcrOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [closeStep, setCloseStep] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/banks").then(r => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const startEdit = (b) => setEditing({ ...editing, [b.id]: { name: b.name, balance: b.balance } });
  const cancelEdit = (id) => {
    const e = { ...editing }; delete e[id]; setEditing(e);
  };
  const saveEdit = async (id) => {
    const v = editing[id];
    try {
      await api.patch(`/banks/${id}`, { name: v.name, balance: Number(v.balance) });
      toast.success("Banco actualizado");
      cancelEdit(id);
      load();
    } catch (e) { toast.error("Error al actualizar"); }
  };

  const addBank = async (e) => {
    e.preventDefault();
    if (!newBank.name.trim()) return toast.error("Nombre requerido");
    try {
      await api.post("/banks", { name: newBank.name, balance: Number(newBank.balance) || 0, order: data.banks.length });
      setNewBank({ name: "", balance: "" });
      load();
    } catch (e) { toast.error("Error al añadir"); }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar esta cuenta?")) return;
    try {
      await api.delete(`/banks/${id}`);
      toast.success("Eliminado");
      load();
    } catch (e) { toast.error("Error"); }
  };

  const cierreSemanal = async () => {
    if (!window.confirm("¿Guardar cierre semanal?\n\n1. Se sincronizará con la API de Proyectos.\n2. Se creará un snapshot del estado actual.\n3. Se archivará el Excel + JSON completo del cashflow.\n\nEsta acción NO borra datos — solo los archiva. Podrás verlos en 'Archivos semanales'.")) return;
    setClosing(true);
    setCloseStep("Sincronizando con Proyectos…");
    try {
      const r = await api.post("/cierre-semanal", {}, { timeout: 120000 });
      const d = r.data || {};
      const archives = d.archives || [];
      const steps = d.steps || [];
      const syncMsg = d.sync?.inserted != null ? `${d.sync.inserted} movimientos` : (d.sync?.error ? `sync falló (${d.sync.error})` : "sync omitido");
      setCloseStep("");
      if (archives.length > 0) {
        toast.success(`Cierre semanal completado · ${syncMsg} · ${archives.length} archivo${archives.length !== 1 ? "s" : ""} guardado${archives.length !== 1 ? "s" : ""}`, { duration: 6000 });
        // Redirige a /archivos para que vea el resultado
        setTimeout(() => nav("/archivos"), 800);
      } else {
        toast.warning(`Cierre semanal parcial: ${steps.join(" → ")}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error en cierre semanal");
    } finally { setClosing(false); setCloseStep(""); }
  };

  return (
    <div className="p-8 max-w-4xl" data-testid="banks-page">
      <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">Bancos</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Saldos de cuentas.</h1>
        </div>
        <button onClick={() => setOcrOpen(true)} className="bg-black text-white px-4 py-2 text-sm inline-flex items-center gap-2 hover:bg-gray-800" data-testid="open-ocr">
          <Camera size={14}/> Actualizar desde pantallazo
        </button>
      </div>

      <div className="border border-[var(--border)] bg-white p-6 mb-6">
        <div className="font-mono-num text-[10px] uppercase tracking-[0.2em] text-[var(--ink-muted)] mb-1">Saldo total</div>
        <div className="font-display text-4xl font-black tracking-tight" data-testid="total-balance">{formatEURDecimal(data.total)}</div>
      </div>

      {/* CIERRE SEMANAL - acción única que reemplaza al wizard */}
      <div className="border-2 border-black bg-white p-6 mb-6" data-testid="cierre-semanal-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-[280px]">
            <div className="font-mono-num text-[10px] uppercase tracking-[0.25em] text-[var(--ink-muted)]">Cierre semanal</div>
            <h2 className="font-display text-2xl font-black tracking-tight mt-1">Guardar el histórico de esta semana.</h2>
            <p className="text-sm text-[var(--ink-muted)] mt-2 max-w-xl">
              Cuando termines de actualizar los saldos, pulsa este botón para <b>sincronizar con Proyectos</b>,
              tomar un <b>snapshot</b> del cashflow y guardar el <b>Excel histórico</b> en <a href="/archivos" className="underline">Archivos semanales</a>.
            </p>
            <p className="text-xs text-[var(--ink-muted)] mt-2 flex items-center gap-1"><CheckCircle2 size={12} className="text-emerald-600"/> No borra nada — solo archiva.</p>
          </div>
          <button
            data-testid="cierre-semanal-button"
            onClick={cierreSemanal}
            disabled={closing}
            className="bg-black text-white px-5 py-3 text-sm inline-flex items-center gap-2 hover:bg-gray-800 disabled:opacity-40 whitespace-nowrap"
          >
            {closing ? <Loader2 size={16} className="animate-spin"/> : <RefreshCw size={16}/>}
            {closing ? (closeStep || "Procesando…") : "Cerrar semana y archivar"}
          </button>
        </div>
      </div>

      <div className="border border-[var(--border)] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-[var(--hover)] text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-muted)]">
            <tr>
              <th className="text-left px-4 py-3">Cuenta</th>
              <th className="text-right px-4 py-3">Saldo</th>
              <th className="px-4 py-3 w-32"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="3" className="px-4 py-6 text-center text-[var(--ink-muted)]">Cargando…</td></tr>
            ) : data.banks.length === 0 ? (
              <tr><td colSpan="3" className="px-4 py-6 text-center text-[var(--ink-muted)]">Sin cuentas registradas.</td></tr>
            ) : data.banks.map(b => {
              const ed = editing[b.id];
              return (
                <tr key={b.id} className="border-t border-[var(--border)]" data-testid={`bank-row-${b.id}`}>
                  <td className="px-4 py-3">
                    {ed ? (
                      <input className="border border-[var(--border)] px-2 py-1 w-full focus:outline-none focus:border-black" value={ed.name} onChange={(e) => setEditing({ ...editing, [b.id]: { ...ed, name: e.target.value } })} data-testid={`bank-name-input-${b.id}`} />
                    ) : (
                      <span data-testid={`bank-name-${b.id}`}>{b.name}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono-num">
                    {ed ? (
                      <input type="number" step="0.01" className="border border-[var(--border)] px-2 py-1 w-full text-right font-mono-num focus:outline-none focus:border-black" value={ed.balance} onChange={(e) => setEditing({ ...editing, [b.id]: { ...ed, balance: e.target.value } })} data-testid={`bank-balance-input-${b.id}`} />
                    ) : (
                      <span data-testid={`bank-balance-${b.id}`}>{formatEURDecimal(b.balance)}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {ed ? (
                      <div className="inline-flex gap-1">
                        <button onClick={() => saveEdit(b.id)} className="bg-black text-white p-1.5 hover:bg-gray-800" data-testid={`bank-save-${b.id}`}><Check size={14} /></button>
                        <button onClick={() => cancelEdit(b.id)} className="border border-[var(--border)] p-1.5 hover:bg-[var(--hover)]" data-testid={`bank-cancel-${b.id}`}><X size={14} /></button>
                      </div>
                    ) : (
                      <div className="inline-flex gap-1">
                        <button onClick={() => startEdit(b)} className="border border-[var(--border)] px-2 py-1 text-xs hover:bg-[var(--hover)]" data-testid={`bank-edit-${b.id}`}>Editar</button>
                        <button onClick={() => remove(b.id)} className="border border-[var(--border)] p-1.5 hover:bg-red-50 hover:text-red-700 hover:border-red-200" data-testid={`bank-delete-${b.id}`}><Trash2 size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <form onSubmit={addBank} className="border-t border-[var(--border)] bg-[var(--bg)] p-4 flex flex-wrap gap-3 items-end" data-testid="new-bank-form">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-muted)] mb-1">Nombre de la cuenta</label>
            <input className="border border-[var(--border)] bg-white px-3 py-2 w-full focus:outline-none focus:border-black" placeholder="Ej. CAIXA ESTRUCTURA" value={newBank.name} onChange={(e) => setNewBank({ ...newBank, name: e.target.value })} data-testid="new-bank-name" />
          </div>
          <div className="w-40">
            <label className="block text-[10px] uppercase tracking-widest font-mono-num text-[var(--ink-muted)] mb-1">Saldo (€)</label>
            <input type="number" step="0.01" className="border border-[var(--border)] bg-white px-3 py-2 w-full font-mono-num focus:outline-none focus:border-black" placeholder="0.00" value={newBank.balance} onChange={(e) => setNewBank({ ...newBank, balance: e.target.value })} data-testid="new-bank-balance" />
          </div>
          <button type="submit" className="bg-black text-white px-4 py-2 text-sm inline-flex items-center gap-1.5 hover:bg-gray-800" data-testid="add-bank-button">
            <Plus size={14} /> Añadir
          </button>
        </form>
      </div>
      <OcrDialog open={ocrOpen} onClose={() => setOcrOpen(false)} onApplied={() => { setOcrOpen(false); load(); }} />
    </div>
  );
}
